import { CloseOutlined } from "@ant-design/icons";
import { Input } from "antd";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import api from "../api/axios";
import styles from "./ExperiencePrompt.module.css";

const NOTE_MAX_LENGTH = 2000;
const DONE_VISIBLE_MS = 2200;
const DONE_FADE_MS = 500;

const OPTIONS = [
    { value: "beyond_expectations", label: "Further than I expected", prompt: "What's made the biggest difference?" },
    { value: "on_track", label: "Getting there", prompt: "Anything that would make this better?" },
    { value: "too_slow", label: "Too slowly", prompt: "What's slowing you down?" },
    { value: "not_really", label: "Not really", prompt: "What's missing?" },
];

// Plain elements, not antd's Button -- see ContentFlag.jsx for why.
const activateOnKey = (handler) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handler();
    }
};

const ExperiencePrompt = ({ due, journeyTitle }) => {
    const seenSentRef = useRef(false);
    const [hidden, setHidden] = useState(false);
    const [selected, setSelected] = useState(null);
    const [note, setNote] = useState("");
    const [done, setDone] = useState(false);
    const [fadingOut, setFadingOut] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // React StrictMode double-mounts in development -- a naive post here would
        // burn two impressions on a single dashboard load.
        if (!due || seenSentRef.current) return;
        seenSentRef.current = true;

        api.post("/workspace/experience/prompt/seen/").catch((error) => {
            console.error("Error marking experience prompt seen:", error);
        });
    }, [due]);

    useEffect(() => {
        // Auto-dismiss the "Thanks" state: let it sit long enough to read, fade it
        // out, then unmount so the space it held closes back up on its own.
        if (!done) return;
        const fadeTimer = setTimeout(() => setFadingOut(true), DONE_VISIBLE_MS);
        const dismissTimer = setTimeout(() => setDismissed(true), DONE_VISIBLE_MS + DONE_FADE_MS);
        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(dismissTimer);
        };
    }, [done]);

    if (!due || hidden || dismissed) return null;

    const send = async () => {
        try {
            await api.post("/workspace/experience/respond/", { score: selected, note });
            setDone(true);
        } catch (error) {
            console.error("Error sending experience response:", error);
        }
    };

    const notNow = () => setHidden(true);

    if (done) {
        return (
            <div className={classNames(styles.card, { [styles.fadeOut]: fadingOut })}>
                <div className={styles.glow} />
                <div className={styles.doneText}>This helps us build the right thing.</div>
            </div>
        );
    }

    const selectedOption = OPTIONS.find((option) => option.value === selected);

    return (
        <div className={styles.card}>
            <div className={styles.glow} />
            <div className={styles.row}>
                <div className={styles.prompt}>
                    You've been working on <strong>{journeyTitle}</strong>.{" "}
                    {selectedOption ? selectedOption.prompt : "Is this getting you where you wanted to go?"}
                </div>

                <div className={styles.chips}>
                    {OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={classNames(styles.chip, {
                                [styles.chipSelected]: selected === option.value,
                            })}
                            onClick={() => setSelected(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <span
                    role="button"
                    tabIndex={0}
                    aria-label="Not now"
                    className={styles.closeButton}
                    onClick={notNow}
                    onKeyDown={activateOnKey(notNow)}
                >
                    <CloseOutlined />
                </span>
            </div>

            {selectedOption && (
                <div className={styles.expanded}>
                    <Input.TextArea
                        className={styles.note}
                        size="small"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        maxLength={NOTE_MAX_LENGTH}
                        placeholder="(Optional) Tell us more"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <div className={styles.actions}>
                        <span
                            role="button"
                            tabIndex={0}
                            className={styles.sendButton}
                            onClick={send}
                            onKeyDown={activateOnKey(send)}
                        >
                            Send
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExperiencePrompt;
